import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { setToastMessage } from "../../store/slices/app/appSlice";
// import axios from "axios";
import type { components } from "../../types/api";
import { useProcessAmazonReportsMutation } from "../../store/apis/amazonApi";
import {
  useGetInternalCampaignsAdminQuery,
  useMigrateOrphanCampaignsMutation,
} from "../../store/apis/internalCampaignApi";
import { useRepairBriefingsMutation } from "../../store/apis/briefingApi";

interface InternalCampaignsTabProps {
  isAdmin: boolean;
}

interface ProcessorPubSubPushRequest {
  message: {
    data: string;
    messageId: string;
    message_id: string;
    publishTime: string;
    publish_time: string;
  };
  subscription: string;
}

export default function InternalCampaignsTab({
  isAdmin,
}: InternalCampaignsTabProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Table state
  const [page, setPage] = useState(0); // 0-based for UI, API expects 1-based
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(h);
  }, [search]);

  const queryArgs = useMemo(() => {
    const args: Record<string, string | number> = {
      page: page + 1,
      pageSize: rowsPerPage,
    };
    if (debouncedSearch) args.search = debouncedSearch;
    return args;
  }, [page, rowsPerPage, debouncedSearch]);

  const { data, isLoading, isFetching, error, refetch } =
    useGetInternalCampaignsAdminQuery(queryArgs, {
      skip: !isAdmin,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    });

  const paginated = data?.data as
    | components["schemas"]["PaginatedResult_FormattedInternalCampaign_"]
    | undefined;
  const items = paginated?.items ?? [];
  const total = paginated?.total ?? 0;

  // Per-row syncing state
  const [syncingIds, setSyncingIds] = useState<Record<string, boolean>>({});
  const [isProcessingHivestack, setIsProcessingHivestack] = useState(false);
  const [processAmazonReports, { isLoading: isProcessingAmazon }] =
    useProcessAmazonReportsMutation();
  const [migrateOrphans, { isLoading: isMigratingOrphans }] =
    useMigrateOrphanCampaignsMutation();
  const [repairBriefings, { isLoading: isRepairingBriefings }] =
    useRepairBriefingsMutation();
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);
  const [repairBriefingIds, setRepairBriefingIds] = useState("");

  const processorBaseUrl =
    process.env.REACT_APP_PROCESSOR_URL || "http://localhost:8081";

  const handleMigrateOrphans = async () => {
    if (!isAdmin || isMigratingOrphans) return;

    try {
      const result = await migrateOrphans().unwrap();
      if (result.data) {
        const { totalAffected, totalOrphans } = result.data;
        if (totalAffected === 0) {
          dispatch(
            setToastMessage({
              message: t("processor.migration.noOrphans", {
                defaultValue: "No orphaned campaigns found. Database is clean.",
              }),
              severity: "success",
            })
          );
        } else {
          dispatch(
            setToastMessage({
              message: t("processor.migration.success", {
                defaultValue:
                  "Migrated {{orphans}} internal campaigns across {{affected}} parent campaigns.",
                orphans: totalOrphans,
                affected: totalAffected,
              }),
              severity: "success",
            })
          );
          refetch();
        }
      }
    } catch (error) {
      console.error("Migration error:", error);
      dispatch(
        setToastMessage({
          message: t("processor.migration.error", {
            defaultValue: "Failed to migrate orphaned campaigns.",
          }),
          severity: "error",
        })
      );
    }
  };

  const handleRepairBriefings = async () => {
    const ids = repairBriefingIds
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length === 0) return;

    try {
      const result = await repairBriefings({ briefingIds: ids }).unwrap();
      if (result.data) {
        const { totalRepaired, latestVersionFixes, details } = result.data;
        const versionNote =
          latestVersionFixes > 0
            ? t("processor.repair.latestVersionFixed", {
                defaultValue:
                  "Latest-version flag fixed on {{count}} briefing(s).",
                count: latestVersionFixes,
              })
            : "";
        if (totalRepaired === 0 && versionNote) {
          dispatch(
            setToastMessage({ message: versionNote, severity: "success" })
          );
        } else if (totalRepaired === 0) {
          dispatch(
            setToastMessage({
              message: t("processor.repair.noRepairs", {
                defaultValue:
                  "No repairs needed. All {{count}} briefings are healthy.",
                count: details.length,
              }),
              severity: "success",
            })
          );
        } else {
          const repaired = t("processor.repair.success", {
            defaultValue:
              "Repaired {{total}} associated campaigns across {{count}} briefings.",
            total: totalRepaired,
            count: details.length,
          });
          dispatch(
            setToastMessage({
              message: versionNote ? `${repaired} ${versionNote}` : repaired,
              severity: "success",
            })
          );
        }
        setRepairDialogOpen(false);
        setRepairBriefingIds("");
      }
    } catch (error) {
      console.error("Repair error:", error);
      dispatch(
        setToastMessage({
          message: t("processor.repair.error", {
            defaultValue: "Failed to repair briefings.",
          }),
          severity: "error",
        })
      );
    }
  };

  const handleSync = async (
    it: components["schemas"]["FormattedInternalCampaign"]
  ) => {
    const timezone = it.timezone || "UTC";
    if (!it.startDate || !it.endDate) {
      dispatch(
        setToastMessage({
          message: t(
            "processor.missingDates",
            "Cannot sync: start and end dates are required"
          ),
          severity: "warning",
        })
      );
      return;
    }

    if (!it.internalId) {
      dispatch(
        setToastMessage({
          message: t(
            "processor.missingDv360CampaignId",
            "Cannot sync: DV360 campaign ID is required in campaign settings"
          ),
          severity: "warning",
        })
      );
      return;
    }

    try {
      setSyncingIds((prev) => ({ ...prev, [it.id]: true }));

      const rawPayload = {
        internalCampaignId: it.id,
        internalPlatform: it.internalPlatform,
        startDate: it.startDate,
        endDate: it.endDate,
        timezone,
      };

      const dataBase64 = btoa(JSON.stringify(rawPayload));

      const body: ProcessorPubSubPushRequest = {
        message: {
          data: dataBase64,
          messageId: "1",
          message_id: "1",
          publishTime: dayjs().format("YYYY-MM-DD"),
          publish_time: dayjs().format("YYYY-MM-DD"),
        },
        subscription: "test",
      };

      const url = `${processorBaseUrl}/metrics/process-internal-campaign`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data?.success) {
        dispatch(
          setToastMessage({
            message: t("processor.syncSuccess", "Sync triggered successfully"),
            severity: "success",
          })
        );
      } else {
        const errorMsg =
          data?.message ||
          data?.details ||
          t("processor.syncFailed", "Sync did not return success=true");
        dispatch(
          setToastMessage({
            message: errorMsg,
            severity: "error",
          })
        );
      }
    } catch (e) {
      console.error(e);
      dispatch(
        setToastMessage({
          message: t("processor.syncError", "Failed to trigger sync"),
          severity: "error",
        })
      );
    } finally {
      setSyncingIds((prev) => ({ ...prev, [it.id]: false }));
    }
  };

  const handleProcessAmazonReports = async () => {
    if (!isAdmin || isProcessingAmazon) {
      return;
    }

    try {
      const result = await processAmazonReports().unwrap();

      dispatch(
        setToastMessage({
          message: t("processor.amazon.success", {
            defaultValue:
              "Amazon reports processed (processed {{processed}}, completed {{completed}}, failed {{failed}}, timeout {{timeout}})",
            processed: result?.processed ?? 0,
            completed: result?.completed ?? 0,
            failed: result?.failed ?? 0,
            timeout: result?.timeout ?? 0,
          }),
          severity: "success",
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: t("processor.amazon.error", {
            defaultValue: "Failed to process Amazon reports",
          }),
          severity: "error",
        })
      );
    }
  };

  const handleProcessHivestackReports = async () => {
    if (!isAdmin || isProcessingHivestack) {
      return;
    }

    try {
      setIsProcessingHivestack(true);
      const url = `${processorBaseUrl}/metrics/process-hivestack-reports`;
      const res = await fetch(url, { method: "POST" });
      const result = await res.json();

      dispatch(
        setToastMessage({
          message: t("processor.hivestack.success", {
            defaultValue:
              "Hivestack reports processed (processed {{processed}}, completed {{completed}}, failed {{failed}}, timeout {{timeout}})",
            processed: result?.processed ?? 0,
            completed: result?.completed ?? 0,
            failed: result?.failed ?? 0,
            timeout: result?.timeout ?? 0,
          }),
          severity: "success",
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: t("processor.hivestack.error", {
            defaultValue: "Failed to process Hivestack reports",
          }),
          severity: "error",
        })
      );
    } finally {
      setIsProcessingHivestack(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h3" gutterBottom>
            {t("processor.title", "Processor")}
          </Typography>
        </Box>
        {isAdmin && (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleMigrateOrphans}
              disabled={isMigratingOrphans}
              sx={{ textTransform: "none", borderRadius: 6 }}
              startIcon={
                isMigratingOrphans ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isMigratingOrphans
                ? t("processor.migration.processing", "Migrating...")
                : t("processor.migration.processButton", "Migrate orphans")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setRepairDialogOpen(true)}
              sx={{ textTransform: "none", borderRadius: 6 }}
            >
              {t("processor.repair.openButton", "Repair briefings")}
            </Button>
            <Button
              variant="outlined"
              onClick={handleProcessAmazonReports}
              disabled={isProcessingAmazon}
              sx={{ textTransform: "none", borderRadius: 6 }}
              startIcon={
                isProcessingAmazon ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isProcessingAmazon
                ? t("processor.amazon.processing", "Processing...")
                : t("processor.amazon.processButton", "Process Amazon reports")}
            </Button>
            <Button
              variant="contained"
              onClick={handleProcessHivestackReports}
              disabled={isProcessingHivestack}
              sx={{ textTransform: "none", borderRadius: 6 }}
              startIcon={
                isProcessingHivestack ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isProcessingHivestack
                ? t("processor.hivestack.processing", "Processing...")
                : t(
                    "processor.hivestack.processButton",
                    "Process Hivestack reports"
                  )}
            </Button>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {!isAdmin ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary">
            {t(
              "common.unauthorized",
              "You are not authorized to view this page."
            )}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          {/* Search */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <TextField
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={t(
                "processor.search",
                "Search by name or internal ID"
              )}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 360 }}
            />
            <Button
              variant="outlined"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={
                isFetching ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )
              }
              sx={{ textTransform: "none", borderRadius: 6 }}
            >
              {t("common.refresh", { defaultValue: "Refresh" })}
            </Button>
          </Box>

          <Paper
            sx={{
              boxShadow: "none",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="error">
                  {t("processor.error", "Failed to load internal campaigns")}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t("internalCampaigns.name", {
                            defaultValue: "Name",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("internalCampaigns.platform", {
                            defaultValue: "Platform",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("internalCampaigns.internalId", {
                            defaultValue: "Internal ID",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("internalCampaigns.margin", {
                            defaultValue: "Margin",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("internalCampaigns.startDate", {
                            defaultValue: "Start Date",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("internalCampaigns.endDate", {
                            defaultValue: "End Date",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("internalCampaigns.lastDispatchedAt", {
                            defaultValue: "Last Dispatched",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("internalCampaigns.currency", {
                            defaultValue: "Currency",
                          })}
                        </TableCell>
                        <TableCell>
                          {t("common.actions", { defaultValue: "Actions" })}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography variant="body2" color="text.secondary">
                              {t(
                                "processor.noResults",
                                "No internal campaigns found"
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        items.map((it) => (
                          <TableRow key={it.id} hover>
                            <TableCell>{it.displayName}</TableCell>
                            <TableCell>{it.internalPlatform}</TableCell>
                            <TableCell>{it.internalId ?? "-"}</TableCell>
                            <TableCell>{it.margin}</TableCell>
                            <TableCell>
                              {it.startDate
                                ? dayjs(it.startDate).format("LL")
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {it.endDate
                                ? dayjs(it.endDate).format("LL")
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {it.lastDispatchedAt
                                ? dayjs(it.lastDispatchedAt).fromNow()
                                : "-"}
                            </TableCell>
                            <TableCell>{it.currency}</TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!!syncingIds[it.id]}
                                onClick={() => handleSync(it)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 6,
                                }}
                              >
                                {syncingIds[it.id]
                                  ? t("processor.syncing", "Syncing...")
                                  : t("processor.sync", "Sync")}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => {
                    const v = Number(e.target.value);
                    setRowsPerPage(v);
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </>
            )}
          </Paper>
        </Box>
      )}

      {/* Repair Briefings Dialog */}
      <Dialog
        open={repairDialogOpen}
        onClose={() => setRepairDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("processor.repair.dialogTitle", "Repair Broken Briefings")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "processor.repair.dialogDescription",
              "Paste briefing IDs (one per line or comma-separated). This will reset stuck FINALIZED/LINKED briefings back to IN_PRODUCTION so you can re-run 'Create Campaigns'."
            )}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder={
              "665aff54-0139-4eab-945a-519afaa2b489\nf95532d2-ef60-4115-b4e4-5257732e386c"
            }
            value={repairBriefingIds}
            onChange={(e) => setRepairBriefingIds(e.target.value)}
            sx={{ fontFamily: "monospace" }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRepairDialogOpen(false)} variant="outlined">
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={
              isRepairingBriefings || repairBriefingIds.trim().length === 0
            }
            onClick={handleRepairBriefings}
            startIcon={
              isRepairingBriefings ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {isRepairingBriefings
              ? t("processor.repair.repairing", "Repairing...")
              : t("processor.repair.repairButton", "Repair")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
